import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FieldError,
  HelperText,
  Input,
  Label,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
} from '@/components/ui';
import { CalendarDays, CreditCard, Hash, KeyRound, Send, UserRound } from 'lucide-react';
import { partnerApi } from '@/lib/api-affiliate';

const generalSchema = z.object({
  displayName: z.string().max(120).optional(),
  telegramContact: z.string().max(120).optional(),
});
type GeneralValues = z.infer<typeof generalSchema>;

const paymentSchema = z.object({
  paymentMethod: z.enum(['usdt_trc20', 'usdt_bep20']).optional(),
  paymentAddress: z.string().max(500).optional(),
});
type PaymentValues = z.infer<typeof paymentSchema>;

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Укажите текущий пароль'),
    newPassword: z.string().min(8, 'Минимум 8 символов'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Пароли не совпадают',
  });
type PasswordValues = z.infer<typeof passwordSchema>;

/** One labelled metadata row in the identity sidebar. */
function MetaRow({
  icon,
  label,
  value,
  mono,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[var(--radius-md)] bg-[var(--bg-elevated)] text-[var(--accent-bright)]">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[var(--text-muted)]">
          {label}
        </div>
        <div
          className={
            'truncate text-[13px] text-[var(--text)] ' + (mono ? 'font-mono' : '')
          }
        >
          {value}
        </div>
      </div>
    </div>
  );
}

export function ProfilePage() {
  const me = partnerApi.me.useGet();
  const updateProfile = partnerApi.profile.useUpdate();
  const changePassword = partnerApi.profile.useChangePassword();

  const general = useForm<GeneralValues>({ resolver: zodResolver(generalSchema) });
  const payment = useForm<PaymentValues>({ resolver: zodResolver(paymentSchema) });
  const password = useForm<PasswordValues>({ resolver: zodResolver(passwordSchema) });

  useEffect(() => {
    if (me.data) {
      general.reset({
        displayName: me.data.displayName ?? '',
        telegramContact: me.data.telegramContact ?? '',
      });
      payment.reset({
        // Только актуальные методы попадают в форму; legacy-значения сбрасываются.
        paymentMethod:
          me.data.paymentMethod === 'usdt_trc20' || me.data.paymentMethod === 'usdt_bep20'
            ? me.data.paymentMethod
            : undefined,
        paymentAddress: me.data.paymentAddress ?? '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.data]);

  const saveGeneral = async (v: GeneralValues) => {
    await updateProfile.mutateAsync(v);
    toast.success('Профиль обновлён');
  };
  const savePayment = async (v: PaymentValues) => {
    await updateProfile.mutateAsync(v);
    toast.success('Реквизиты обновлены');
  };
  const savePassword = async (v: PasswordValues) => {
    await changePassword.mutateAsync({
      currentPassword: v.currentPassword,
      newPassword: v.newPassword,
    });
    toast.success('Пароль изменён');
    password.reset();
  };

  return (
    <div className="mx-auto w-full max-w-6xl">
      <PageHeader title="Профиль" subtitle="Личные данные и платёжные настройки" />

      <div className="grid gap-5 lg:grid-cols-[340px_minmax(0,1fr)] lg:items-start">
        {/* ---- Identity sidebar ---- */}
        <aside className="lg:sticky lg:top-6">
          {me.data ? (
            <Card className="overflow-hidden">
              {/* Gradient banner */}
              <div
                aria-hidden
                className="h-[88px] bg-gradient-to-br from-[var(--accent-soft)] via-[var(--accent-soft)]/50 to-transparent"
              />
              {/* Avatar + identity, pulled up over the banner */}
              <div className="-mt-12 flex flex-col items-center px-5 pb-5 text-center">
                <div className="grid h-24 w-24 shrink-0 place-items-center rounded-2xl bg-[var(--accent)] text-[26px] font-bold uppercase text-[var(--accent-fg)] shadow-lg ring-4 ring-[var(--bg-raised)]">
                  {(me.data.displayName?.trim() || me.data.email).slice(0, 2)}
                </div>
                <h2 className="mt-3 max-w-full truncate text-[18px] font-semibold tracking-tight text-[var(--text)]">
                  {me.data.displayName?.trim() || me.data.email.split('@')[0]}
                </h2>
                <p className="mt-0.5 max-w-full truncate text-[13px] text-[var(--text-muted)]">
                  {me.data.email}
                </p>
                <span className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-[var(--success-soft)] px-2.5 py-1 text-[11px] font-semibold text-[var(--success)]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--success)]" />
                  Активен
                </span>
              </div>
              {/* Metadata list */}
              <div className="space-y-3 border-t border-[var(--border)] px-5 py-4">
                <MetaRow
                  icon={<Hash size={15} />}
                  label="Партнёрский код"
                  value={me.data.affiliateCode || '—'}
                  mono
                />
                <MetaRow
                  icon={<CalendarDays size={15} />}
                  label="В системе с"
                  value={new Date(me.data.createdAt).toLocaleDateString('ru-RU', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                />
                <MetaRow
                  icon={<Send size={15} />}
                  label="Telegram"
                  value={
                    me.data.telegramContact || (
                      <span className="text-[var(--text-muted)]">Не указан</span>
                    )
                  }
                />
              </div>
            </Card>
          ) : (
            <Card>
              <div className="h-[88px] bg-[var(--bg-elevated)]" />
              <div className="-mt-12 flex flex-col items-center px-5 pb-5">
                <Skeleton className="h-24 w-24 rounded-2xl" />
                <Skeleton className="mt-3 h-5 w-32" />
                <Skeleton className="mt-2 h-4 w-44" />
              </div>
            </Card>
          )}
        </aside>

        {/* ---- Settings forms ---- */}
        <div className="space-y-5">
          <div className="grid gap-5 md:grid-cols-2 md:items-start">
            {/* General */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserRound size={15} className="text-[var(--accent-bright)]" />
                  Основное
                </CardTitle>
              </CardHeader>
              <form onSubmit={general.handleSubmit(saveGeneral)}>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="displayName">Имя</Label>
                    <Input
                      id="displayName"
                      placeholder="Как к вам обращаться"
                      {...general.register('displayName')}
                    />
                    <FieldError message={general.formState.errors.displayName?.message} />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" value={me.data?.email ?? ''} readOnly disabled />
                    <HelperText>Email нельзя изменить — обратитесь в поддержку, если нужно сменить.</HelperText>
                  </div>
                  <div>
                    <Label htmlFor="telegramContact">Telegram</Label>
                    <Input
                      id="telegramContact"
                      placeholder="@username"
                      {...general.register('telegramContact')}
                    />
                    <FieldError message={general.formState.errors.telegramContact?.message} />
                  </div>
                  <div className="flex justify-end pt-1">
                    <Button
                      type="submit"
                      variant="primary"
                      loading={updateProfile.isPending && general.formState.isSubmitting}
                    >
                      Сохранить
                    </Button>
                  </div>
                </CardContent>
              </form>
            </Card>

            {/* Payment */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard size={15} className="text-[var(--accent-bright)]" />
                  Платёжные реквизиты
                </CardTitle>
              </CardHeader>
              <form onSubmit={payment.handleSubmit(savePayment)}>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="paymentMethod">Метод</Label>
                    <Select id="paymentMethod" {...payment.register('paymentMethod')}>
                      <option value="">— не выбрано —</option>
                      <option value="usdt_trc20">USDT · TRC20</option>
                      <option value="usdt_bep20">USDT · BEP20</option>
                    </Select>
                    <HelperText>Используется по умолчанию при запросе выплаты.</HelperText>
                  </div>
                  <div>
                    <Label htmlFor="paymentAddress">Адрес / реквизиты</Label>
                    <Textarea
                      id="paymentAddress"
                      placeholder="TXxxx… или IBAN…"
                      rows={3}
                      {...payment.register('paymentAddress')}
                    />
                    <FieldError message={payment.formState.errors.paymentAddress?.message} />
                    <HelperText>Проверяйте адрес внимательно — переводы криптовалюты необратимы.</HelperText>
                  </div>
                  <div className="flex justify-end pt-1">
                    <Button
                      type="submit"
                      variant="primary"
                      loading={updateProfile.isPending && payment.formState.isSubmitting}
                    >
                      Сохранить
                    </Button>
                  </div>
                </CardContent>
              </form>
            </Card>
          </div>

          {/* Password — full width, fields laid out horizontally on wide screens */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <KeyRound size={15} className="text-[var(--accent-bright)]" />
                Пароль
              </CardTitle>
            </CardHeader>
            <form onSubmit={password.handleSubmit(savePassword)}>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="currentPassword">Текущий пароль</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    autoComplete="current-password"
                    {...password.register('currentPassword')}
                  />
                  <FieldError message={password.formState.errors.currentPassword?.message} />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="newPassword">Новый пароль</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      autoComplete="new-password"
                      {...password.register('newPassword')}
                    />
                    <FieldError message={password.formState.errors.newPassword?.message} />
                    <HelperText>Минимум 8 символов. Не используйте уже скомпрометированные пароли.</HelperText>
                  </div>
                  <div>
                    <Label htmlFor="confirmPassword">Повторите</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      {...password.register('confirmPassword')}
                    />
                    <FieldError message={password.formState.errors.confirmPassword?.message} />
                  </div>
                </div>
                <div className="flex justify-end pt-1">
                  <Button type="submit" variant="primary" loading={changePassword.isPending}>
                    Изменить пароль
                  </Button>
                </div>
              </CardContent>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
